import React, { useState, useEffect, useCallback } from 'react';
import { getEntityId } from 'helpers/entityId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { UilUserPlus } from '@iconscout/react-unicons';
import { useMutation, useQueryClient } from 'react-query';
import { Modal, Row, Col, Form, FormControl, Button } from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import ReactSelect from 'components/base/ReactSelect';
import { EmployeeAvatarDropdown } from 'components/common/AvatarDropdown';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { ToDoItem } from 'data/project-management/todoListData';
import {
  CreateTaskFormData,
  CreateTaskDTO
} from 'data/from-data/create-task-form.data';
import { Employee } from 'data/users';
import {
  ASSIGN_TASK,
  CREATE_TASK,
  DELETE_TASK,
  SET_DESCRIPTION,
  UPDATE_TASK
} from 'services/api/task';
import { EmployeeOption } from '../card-view/types/types';

interface TodoItemDetailsModalProps {
  handleClose: () => void;
  item: ToDoItem | null;
  showCreate?: boolean;
  jobId?: string;
  supervisors?: Employee[];
  employeeOptions?: EmployeeOption[];
  employees?: Employee[];
}

const TodoItemDetailsModal: React.FC<TodoItemDetailsModalProps> = ({
  handleClose,
  item,
  showCreate,
  jobId,
  supervisors,
  employeeOptions,
  employees
}) => {
  const [createTaskDto, setCreateTaskDto] = useState<CreateTaskFormData>(
    new CreateTaskFormData()
  );
  const [showEmployeeDropdown, setShowEmployeeDropdown] =
    useState<boolean>(false);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>(
    []
  );
  const [editMode, setEditMode] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string>('');
  const [taskLabel, setTaskLabel] = useState<string>(
    String(item?.badge?.label)
  );
  const [filteredEmployeeOptions, setFilteredEmployeeOptions] = useState<
    EmployeeOption[]
  >([]);
  const { authUser } = useAccessToken();
  const queryClient = useQueryClient();

  useEffect(() => {
    setTaskLabel(String(item?.badge?.label));
  }, [item?.badge?.label]);

  useEffect(() => {
    if (item && employees) {
      const assignedEmployeeIds = new Set(
        item?.assignedTo?.map(emp => getEntityId(emp))
      );
      const unassignedEmployees = employees.filter(
        emp => !assignedEmployeeIds.has(getEntityId(emp))
      );
      const filteredOptions = unassignedEmployees.map(emp => ({
        value: getEntityId(emp) || '',
        label: `${emp.firstName} ${emp.lastName}`
      }));
      setFilteredEmployeeOptions(filteredOptions);
    } else {
      setFilteredEmployeeOptions(employeeOptions || []);
    }
  }, [item, employees, employeeOptions]);

  const handleEmployeeChange = useCallback(
    (selectedOptions: EmployeeOption[] | null | unknown) => {
      setSelectedEmployees(selectedOptions as EmployeeOption[]);
    },
    []
  );
  const createTask = useMutation('createTask', CREATE_TASK, {
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries('getJobs');
    }
  });

  const assignJob = useMutation('assignJob', ASSIGN_TASK, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      if (item) {
        item.assignedTo = [
          ...(item.assignedTo || []),
          ...(employees?.filter(employee =>
            selectedEmployees?.some(
              selected => selected.value === getEntityId(employee)
            )
          ) || [])
        ];
      }
      setShowEmployeeDropdown(false);
      setSelectedEmployees([]);
    },
    onError: (error: TypeError) => {
      setError(error.message);
    }
  });

  const updateTask = useMutation('updateTask', UPDATE_TASK, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
    }
  });

  const descriptionMutation = useMutation('setDescription', SET_DESCRIPTION, {
    onSuccess: (data: any) => {
      queryClient.invalidateQueries('getJobs');
      setEditMode(false);
      setDescription('');
      if (item) {
        item.description = data.data?.description;
      }
    },
    onError: (error: TypeError) => {
      setError(error.message);
    }
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const { task, date, time } = createTaskDto;
      const payload: CreateTaskDTO = {
        ...createTaskDto,
        task: task.value,
        jobId: jobId as string,
        creator: getEntityId(authUser) as string,
        date: date.value,
        time: time.value
      };
      createTask.mutate(payload);
    },
    [createTaskDto, jobId, authUser, createTask]
  );

  const handleAssignToEmployees = useCallback(() => {
    assignJob.mutate({
      taskId: String(getEntityId(item)),
      employeeIds: selectedEmployees.map(emp => emp.value)
    });
  }, [assignJob, item, selectedEmployees]);

  const handleDelete = useCallback(
    async (itemId: string) => {
      if (window.confirm('Are you sure you want to delete this task?')) {
        await DELETE_TASK(itemId);
        handleClose();
        queryClient.invalidateQueries('getJobs');
      }
    },
    [handleClose, queryClient]
  );

  const setTaskDescription = useCallback(() => {
    descriptionMutation.mutate({
      formData: { description },
      taskId: String(getEntityId(item))
    });
  }, [descriptionMutation, description, item]);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value } = e.target;
      setCreateTaskDto(prevData => ({
        ...prevData,
        [name]: {
          ...prevData[name],
          value:
            name === 'badge'
              ? {
                  label: value,
                  bg: badgeOptions[value as keyof typeof badgeOptions]
                }
              : value
        }
      }));
    },
    []
  );

  const handleDateInputChange = useCallback((value: string, name: string) => {
    setCreateTaskDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  }, []);

  const onChangeTaskDetails = useCallback(
    (value: any, name: string) => {
      const updateData =
        name === 'badge'
          ? {
              [name]: {
                label: value,
                bg: badgeOptions[value as keyof typeof badgeOptions]
              },
              completed:
                value === 'COMPLETED' || value === 'CLOSED' ? true : false
            }
          : { [name]: value };
      if (name === 'badge') {
        setTaskLabel(value);
      }
      updateTask.mutate({
        taskId: String(getEntityId(item)),
        data: updateData
      });
    },
    [updateTask, item]
  );

  const badgeOptions = {
    DRAFT: 'primary',
    URGENT: 'warning',
    PROGRESS: 'info',
    PENDING: 'info',
    COMPLETED: 'success',
    CANCELLED: 'danger',
    CLOSED: 'success',
    ARCHIVED: 'secondary'
  };

  return (
    <Modal show={!!item || !!showCreate} onHide={handleClose} size="xl">
      <Modal.Header className="px-6 py-5 border-300 pe-sm-5 px-md-6 dark__bg-1100">
        <h3 className="text-1000 fw-bolder mb-0">
          {item ? item.task : 'Create a Task'}
        </h3>
        <Button
          variant="phoenix-secondary"
          onClick={handleClose}
          className="btn-icon btn-icon-xl flex-shrink-0"
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </Modal.Header>
      <Modal.Body className="bg-100 px-5 px-sm-6 py-0 rounded-bottom">
        <Row className="gx-14">
          {item ? (
            <>
              <Col xs={12} lg={7} className="border-end-lg border-300">
                {/* Description section */}
                <div className="mt-6 mb-7">
                  <div className="mb-7">
                    <div className="d-flex align-items-center mb-3">
                      <h4 className="text-900 me-3">Description</h4>
                      {supervisors?.find(
                        employee =>
                          getEntityId(employee) === getEntityId(authUser)
                      ) && (
                        <Button
                          variant="link"
                          className="text-decoration-none p-0"
                          onClick={() => setEditMode(!editMode)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                      )}
                      <div className="ms-2">{item.description}</div>
                    </div>
                    {editMode ? (
                      <>
                        <FormControl
                          as="textarea"
                          rows={4}
                          className="mb-3"
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                        />
                        <div className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            className="px-4"
                            onClick={setTaskDescription}
                          >
                            Save
                          </Button>
                          <Button
                            variant="phoenix-secondary"
                            size="sm"
                            onClick={() => setEditMode(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-1000 mb-0">{item.description}</p>
                    )}
                  </div>
                </div>
                {/* Assignees section */}
                <div className="mb-3">
                  <h6 className="text-800 mb-2">Assignees</h6>
                  <div className="d-flex gap-1">
                    {item.assignedTo?.slice(0, 5).map((member: Employee) => (
                      <EmployeeAvatarDropdown
                        user={member}
                        size="m"
                        key={getEntityId(member)}
                      />
                    ))}
                    {supervisors?.find(
                      employee =>
                        getEntityId(employee) === getEntityId(authUser)
                    ) && (
                      <Button
                        variant="phoenix-secondary"
                        className="btn-circle"
                        size="sm"
                        onClick={() =>
                          setShowEmployeeDropdown(!showEmployeeDropdown)
                        }
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    )}
                  </div>
                  {showEmployeeDropdown && (
                    <Col xs={12}>
                      <div className="mb-3 mt-5">
                        <ReactSelect
                          options={filteredEmployeeOptions}
                          className="mb-5"
                          isMulti
                          name="assignedTo"
                          value={selectedEmployees}
                          onChange={handleEmployeeChange}
                          placeholder="Select Employee"
                          icon={
                            <UilUserPlus
                              className="react-select-icon text-700"
                              size={16}
                            />
                          }
                        />
                      </div>
                      <Button
                        onClick={handleAssignToEmployees}
                        variant="phoenix-primary"
                        className="px-6 ms-auto"
                      >
                        Assign
                      </Button>
                      {assignJob.isError && <div>{error}</div>}
                    </Col>
                  )}
                </div>
              </Col>
              <Col xs={12} lg={5}>
                {/* Other Information section */}
                <div className="mt-lg-6 mb-5 mb-sm-6">
                  <h4 className="mb-5 text-black">Other Information</h4>
                  <h5 className="text-1000 mb-2">Status</h5>
                  <Form.Select
                    className="mb-4"
                    name="badge"
                    value={taskLabel}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      onChangeTaskDetails(e.target.value, e.target.name);
                    }}
                  >
                    {item.badge?.label && (
                      <option value={item.badge.label}>
                        {item.badge.label}
                      </option>
                    )}
                    {[
                      'PENDING',
                      'PROGRESS',
                      'URGENT',
                      'DRAFT',
                      'COMPLETED',
                      'CLOSED'
                    ].map(
                      option =>
                        option !== item.badge?.label && (
                          <option key={option} value={option}>
                            {option === 'PROGRESS' ? 'IN PROGRESS' : option}
                          </option>
                        )
                    )}
                  </Form.Select>
                  <h5 className="text-1000 mb-2">Due Date</h5>
                  <div className="mb-4">
                    <DatePicker
                      onChange={(value, _currentDateString, self) => {
                        onChangeTaskDetails(
                          value[0] as unknown as string,
                          self._input.name
                        );
                      }}
                      placeholder="Set the due date"
                      value={item.date}
                      name="date"
                    />
                  </div>
                  <div className="text-end">
                    <Button
                      onClick={() => handleDelete(String(getEntityId(item)))}
                      variant="phoenix-danger"
                    >
                      Delete Task
                    </Button>
                  </div>
                </div>
              </Col>
            </>
          ) : (
            <Col xs={12} lg={12}>
              {/* Create Task section */}
              <div className="mt-lg-6 mb-5 mb-sm-6 mt-10">
                <h5 className="text-1000 mb-2">Task</h5>
                <div className="mb-4">
                  <Form.Control
                    type="text"
                    placeholder="Task"
                    name="task"
                    value={createTaskDto.task.value}
                    onChange={handleInputChange}
                  />
                </div>
                <h5 className="text-1000 mb-2">Status</h5>
                <Form.Select
                  name={createTaskDto.badge.name}
                  className="mb-4"
                  onChange={handleInputChange}
                >
                  <option>Select</option>
                  <option value="PENDING">PENDING</option>
                  <option value="PROGRESS">IN PROGRESS</option>
                  <option value="URGENT">URGENT</option>
                  <option value="DRAFT">DRAFT</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CLOSED">Closed</option>
                </Form.Select>
                <h5 className="text-1000 mb-2">Due Date</h5>
                <div className="mb-4">
                  <DatePicker
                    placeholder="Set the due date"
                    name={createTaskDto.date.name}
                    value={createTaskDto.date.value}
                    onChange={(value, _currentDateString, self) => {
                      handleDateInputChange(
                        value[0] as unknown as string,
                        self._input.name
                      );
                    }}
                  />
                </div>
                <div className="text-end">
                  <Button onClick={handleSubmit} variant="phoenix-success">
                    Create Task
                  </Button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default TodoItemDetailsModal;
